import qrcode from 'qrcode-generator';
import { t } from '../i18n';

/**
 * Le billet d'un participant.
 *
 * Un carre a montrer a l'entree, que l'organisateur lit avec sa camera.
 * Il ne remplace pas la liste : il la parcourt plus vite. Celui qui a
 * oublie son telephone se fait cocher a la main, et cela vaut pareil.
 *
 * Ce que le carre contient : l'evenement et le membre, rien d'autre. Pas
 * de secret, et c'est assume — le seul a pouvoir s'en servir est
 * l'organisateur, qui voit le visage et le pseudo de qui il vient de
 * cocher, et qui peut decocher. Un faux billet ne donne donc acces a
 * rien : il ferait au pire apparaitre un nom que l'organisateur ne
 * reconnait pas, sous ses yeux, et qu'il retire d'un appui.
 *
 * Le prefixe distingue nos carres de tous les autres que la camera
 * croisera — une etiquette de colis, un menu de restaurant.
 */

const PREFIXE = 'sirocco:billet';

export interface Billet {
  eventId: string;
  userId: string;
}

export function encoderBillet({ eventId, userId }: Billet): string {
  return `${PREFIXE}/${eventId}/${userId}`;
}

/** Rend `null` si le carre lu n'est pas un des notres. */
export function lireBillet(texte: string): Billet | null {
  const morceaux = texte.trim().split('/');
  if (morceaux.length !== 3 || morceaux[0] !== PREFIXE) return null;
  const [, eventId, userId] = morceaux;
  if (!eventId || !userId) return null;
  return { eventId, userId };
}

/**
 * Le carre, en SVG.
 *
 * Une image plutot qu'un canevas : elle se met a l'echelle sans flou, et
 * un billet se regarde de pres et de loin selon que l'on cherche son
 * telephone ou qu'on le tend. La correction d'erreur reste au niveau le
 * plus bas — le carre est lu sur un ecran, jamais sur un papier froisse,
 * et un carre moins dense se lit de plus loin.
 */
export function billetEnSvg(billet: Billet): string {
  const code = qrcode(0, 'L');
  code.addData(encoderBillet(billet));
  code.make();

  const cases = code.getModuleCount();
  // Une marge de quatre cases est ce que la norme demande : sans elle,
  // beaucoup de lecteurs ne trouvent pas les coins.
  const marge = 4;
  const cote = cases + marge * 2;

  let chemin = '';
  for (let y = 0; y < cases; y += 1) {
    for (let x = 0; x < cases; x += 1) {
      if (code.isDark(y, x)) chemin += `M${x + marge} ${y + marge}h1v1h-1z`;
    }
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${cote} ${cote}" ` +
    `shape-rendering="crispEdges" role="img" aria-label="${t.composants.billet.monBillet}">` +
    `<rect width="${cote}" height="${cote}" fill="#fff"/>` +
    `<path d="${chemin}" fill="#111"/></svg>`
  );
}
